import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import AIAssistantWidget from "./AIAssistantWidget";

const AIAssistantWrapper = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  /** Increment to open Leo from deep links (lease renewal email). */
  const [leoOpenSignal, setLeoOpenSignal] = useState(undefined);

  // Hide AI assistant on public /chat/…, tenant-chat, communication, etc.
  const isPublicLeoChatPage = location.pathname.startsWith("/chat/");
  const isTenantChatPage = location.pathname.startsWith("/tenant-chat");
  const isCommunicationPage =
    location.pathname === "/communication" ||
    location.pathname.startsWith("/communication/");
  const isTenantPaymentPage = location.pathname.startsWith("/pay-rent");
  const isPaymentSuccessPage = location.pathname === "/payment-success";
  const isVendorConversationPage =
    location.pathname.startsWith("/conversations/");
  const isPublicListingPage = location.pathname.startsWith("/lease/");
  const isPublicApplyPage = location.pathname.startsWith("/apply/");
  const isTaxPrepPage = location.pathname === "/tax-prep";

  // Detect if any modal is open by checking for ant-modal-mask elements
  useEffect(() => {
    const checkModalOpen = () => {
      const modalMasks = document.querySelectorAll(".ant-modal-mask");
      setIsModalOpen(modalMasks.length > 0);
    };

    // Check immediately
    checkModalOpen();

    // Use MutationObserver to watch for modal changes
    const observer = new MutationObserver(checkModalOpen);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    // Also check periodically as a fallback
    const interval = setInterval(checkModalOpen, 100);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Lease renewal emails: /any?leaseRenewal=<leaseId> → seed Leo composer + open widget
  useEffect(() => {
    if (!user?.uid) return;
    const params = new URLSearchParams(location.search);
    const leaseId = params.get("leaseRenewal");
    if (!leaseId) return;

    const text = `Help me renew lease ${leaseId} before it expires. Walk me through renewal options, timing, and what to send the tenant.`;
    try {
      sessionStorage.setItem(
        "leo_pending_composer",
        JSON.stringify({ text, at: Date.now() })
      );
    } catch {
      /* ignore */
    }
    setLeoOpenSignal(Date.now());

    params.delete("leaseRenewal");
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true }
    );
  }, [location.search, location.pathname, user?.uid, navigate]);

  // Only show the AI assistant when user is logged in, not on excluded pages, and no modal is open
  return user &&
    !isPublicLeoChatPage &&
    !isTenantChatPage &&
    !isCommunicationPage &&
    !isTenantPaymentPage &&
    !isPaymentSuccessPage &&
    !isVendorConversationPage &&
    !isPublicListingPage &&
    !isPublicApplyPage &&
    !isTaxPrepPage &&
    !isModalOpen ? (
    <AIAssistantWidget isVisible={true} openSignal={leoOpenSignal} />
  ) : null;
};

export default AIAssistantWrapper;
